import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Spin, Badge, message } from 'antd';
import { MdOutlineCancel } from 'react-icons/md';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { RiArrowDropDownLine, RiArrowDropUpLine } from 'react-icons/ri';
import { getLinks } from '../data/sidebar';
import { useStateContext } from '../contexts/ContextProvider';
import { UseFetchCaseCount } from '../modules/workspace/Case/hooks/useFetch';
import { useFetchCompanyForNavbar } from '../modules/back-office/Companies/hooks/useFetch';
import { useTabIndicator } from '../hooks/useTabIndicator';
import { getUserStatus } from '../services/breakService';

const Sidebar = () => {
  const { currentColor, activeMenu, setActiveMenu, screenSize, getProfile } = useStateContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [workStatus, setWorkStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const profile = sessionStorage.getItem('role')
  const location = sessionStorage.getItem('location')
  const jobType = sessionStorage.getItem('jobType')
  const companyName = sessionStorage.getItem('companyName')
  const activeCompanyId = sessionStorage.getItem('activeCompanyId')
  const userId = sessionStorage.getItem('id');

  // สีชื่อบริษัท: อ่านจากรายชื่อบริษัทที่ Navbar ดึงไว้อยู่แล้ว (react-query cache เดียวกัน
  // = ไม่มี request เพิ่ม) เดิมอ่านจาก sessionStorage อย่างเดียว ซึ่งเขียนแค่ตอน login
  // กับตอนสลับบริษัท ทำให้แก้สีแล้วต้อง login ใหม่ถึงจะเห็น
  const { data: companyList } = useFetchCompanyForNavbar();
  const companyColor = useMemo(() => {
    const found = (companyList || []).find(
      (c) => String(c.id) === String(activeCompanyId)
    );
    // ยังโหลดไม่เสร็จ → ใช้ค่าที่เคยเก็บไว้กันกระพริบ · ไม่มีเลย → ขาวเหมือนเดิม
    return found?.themeColor || sessionStorage.getItem('companyColor') || '#ffffff';
  }, [companyList, activeCompanyId]);

  // เก็บค่าล่าสุดไว้ใช้เป็น fallback รอบหน้า (ตอน list ยังโหลดไม่เสร็จ)
  useEffect(() => {
    if (companyColor && companyColor !== '#ffffff') {
      sessionStorage.setItem('companyColor', companyColor);
    }
  }, [companyColor]);

  // โลโก้บริษัท (ใช้เป็น favicon ของแท็บ) — มาจาก list ชุดเดียวกับสี ไม่มี request เพิ่ม
  const companyLogo = useMemo(() => {
    const found = (companyList || []).find(
      (c) => String(c.id) === String(activeCompanyId)
    );
    return found?.logoUrl || null;
  }, [companyList, activeCompanyId]);

  // ใช้ hook เพื่อดึงจำนวนเคส
  const { data: caseCount } = UseFetchCaseCount(userId);

  // แท็บเบราว์เซอร์: (จำนวนงานค้าง) ชื่อบริษัท + ไอคอนเป็นโลโก้บริษัท
  useTabIndicator({
    count: caseCount,
    companyName,
    logoUrl: companyLogo,
  });

  // ดึงสถานะการเข้างาน
  const fetchWorkStatus = async () => {
    try {
      if (userId && profile === '3' && location === 'workspace') {
        setIsLoadingStatus(true);
        const response = await getUserStatus(userId);
        if (response.success) {
          const status = response.result.status;
          if (status === 'Ready' || status === 'Break' || status === 'LunchBreak') {
            setWorkStatus('Ready');
          } else {
            setWorkStatus('Dayoff');
          }
        }
      } else {
        setWorkStatus('Ready'); // สำหรับ admin และ sysadmin
      }
    } catch (error) {
      console.error('Error fetching work status:', error);
      setWorkStatus('Dayoff');
    } finally {
      setIsLoadingStatus(false);
    }
  };

  useEffect(() => {
    fetchWorkStatus();
  }, [userId, profile, location]);

  // ตรวจสอบว่าสามารถเข้าถึง tab ได้หรือไม่
  const canAccessTab = (link) => {
    // ถ้าเป็น admin หรือ sysadmin หรือไม่ได้อยู่ใน workspace ให้เข้าถึงได้เสมอ
    if (profile !== '3' || location !== 'workspace') {
      return true;
    }

    // ถ้าเป็นพนักงานใน workspace ต้องเข้างานก่อน
    if (workStatus === 'Ready') {
      return true;
    }

    return false;
  };

  // จัดการการคลิก tab
  const handleTabClick = (link, e) => {
    if (!canAccessTab(link)) {
      e.preventDefault();
      message.warning('กรุณาเข้างานก่อนเข้าถึงหน้านี้');
      // Redirect ไปหน้าเปล่าๆ เมื่อไม่ได้เข้างาน
      // window.location.href = '/workspace/dashboard';
      return false;
    }
    // ถ้าสามารถเข้าถึงได้ ให้ navigate ไปยัง route
    navigate(`/${link.route}`);
    return true;
  };

  // ใช้ useMemo เพื่อ re-calculate links เมื่อมีการเปลี่ยนแปลง
  const links = useMemo(() => {
    return getLinks(profile, location, caseCount, jobType);
  }, [profile, location, caseCount, jobType]);

  const handleCloseSideBar = () => {
    if (activeMenu !== undefined && screenSize <= 900) {
      setActiveMenu(false);
    }
  };

  const toggleDropdown = (title) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const activeLink = 'flex items-center gap-3 pl-3 pt-2 pb-2 rounded-lg  text-white  text-sm m-1';
  const normalLink = 'flex items-center gap-3 pl-3 pt-2 pb-2 rounded-lg text-sm text-white-700 dark:text-gray-200 dark:hover:text-black hover:bg-gray-600 m-1';
  const disabledLink = 'flex items-center gap-3 pl-3 pt-2 pb-2 rounded-lg text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed m-1';

  if (isLoadingStatus) {
    return (
      <div className="ml-2 h-screen md:overflow-hidden overflow-auto md:hover:overflow-auto pb-8 relative w-60">
        <div className="flex justify-center items-center h-[90vh]">
          <Spin />
        </div>
      </div>
    );
  }

  return (
    <div className="ml-2 h-screen md:overflow-hidden overflow-auto md:hover:overflow-auto pb-8 relative w-60">
      {/* เดิมเป็นโลโก้ megabet ฝังตายตัว ใช้ร่วมกันทุกบริษัท แยกไม่ออกว่ากำลังอยู่บริษัทไหน
          เปลี่ยนเป็นชื่อบริษัท + สีประจำบริษัท (ตั้งตอนสร้าง company)
          ใช้ฟอนต์ระบบล้วน ไม่โหลดจาก CDN — กันหน้าค้างถ้า CDN ล่ม และไม่เพิ่มเวลาโหลด */}
      <div className='mt-6 mb-3 px-3 flex flex-col items-center justify-center select-none'>
        <span
          style={{
            color: companyColor,
            // ชื่อยาวลดขนาดลงเป็นขั้น ๆ ไม่ให้ล้นแถบกว้าง 240px
            fontSize: !companyName ? '26px'
              : companyName.length > 16 ? '19px'
              : companyName.length > 11 ? '24px'
              : '31px',
            fontFamily: '"Avenir Next", "Helvetica Neue", Inter, "Segoe UI", system-ui, sans-serif',
            fontWeight: 900,
            fontStyle: 'italic',          // เอียงนิด ๆ ให้ดูมีจังหวะ ไม่แข็งทื่อ
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.05,
            textAlign: 'center',
            wordBreak: 'break-word',
            // เรืองแสงอ่อน ๆ ด้วยสีตัวเอง ทำให้ตัวอักษรลอยขึ้นจากพื้นดำ
            textShadow: `0 0 18px ${companyColor}55, 0 2px 3px rgba(0,0,0,0.65)`,
          }}
        >
          {companyName || 'Backoffice'}
        </span>
        {/* ขีดคั่นไล่สีจากจาง→เข้ม→จาง ปิดท้ายให้ดูเป็นโลโก้ */}
        <span
          style={{
            marginTop: '10px',
            width: '68%',
            height: '2px',
            borderRadius: '2px',
            background: `linear-gradient(90deg, transparent, ${companyColor}, transparent)`,
            opacity: 0.75,
          }}
        />
      </div>
      {activeMenu && (
        <>
          <div className="flex justify-between items-center">
            <div to="/" onClick={handleCloseSideBar} className="items-center gap-3 ml-2 mt-3 flex text-lg font-extrabold tracking-tight dark:text-white text-slate-900">
              {/* <img className='ml-2 w-[100px]' alt=""src='https://cdn.pixabay.com/photo/2017/03/16/21/18/logo-2150297_640.png' /> */}
              <span style={{ fontSize: '24px', marginTop: '15px' }} className='ml-2 text-[#ffffff]' >{location}</span>
            </div>

            <TooltipComponent content="Menu" position="BottomCenter">
              <button
                type="button"
                onClick={() => setActiveMenu(!activeMenu)}
                style={{ color: currentColor }}
                className="text-xl rounded-full p-3 hover:bg-light-gray mt-4 block md:hidden"
              >
                <MdOutlineCancel />
              </button>
            </TooltipComponent>
          </div>

          <div className="mt-6">
            {getProfile ? links.map((item) => (
              <div key={item.title}>
                <p className="text-gray-400 dark:text-gray-400 m-2 mt-3 uppercase text-xs">
                  {item.title}
                </p>
                {item.links.map((link) => (
                  link.subLinks ? (
                    <div key={link.name}>
                      <div
                        onClick={() => {
                          if (canAccessTab(link)) {
                            toggleDropdown(link.name);
                          } else {
                            message.warning('กรุณาเข้างานก่อนเข้าถึงหน้านี้');
                          }
                        }}
                        className={`${canAccessTab(link) ? normalLink : disabledLink} cursor-pointer flex justify-between items-center`}
                      >
                        <div className='flex items-center '>
                          {link.icon}
                          <span className="capitalize ml-2 bg text-sm">{link.name}</span>
                        </div>
                        {dropdownOpen[link.name] ? <RiArrowDropUpLine /> : <RiArrowDropDownLine />}
                      </div>
                      {dropdownOpen[link.name] && (
                        <div className='pl-6 pr-2'>
                          <div className="bg-gray rounded-lg z-10 pb-1">
                            {link.subLinks.map((subLink) => (
                              <div
                                key={subLink.name}
                                onClick={(e) => {
                                  if (handleTabClick(subLink, e)) {
                                    handleCloseSideBar();
                                  }
                                }}
                                className={canAccessTab(subLink) ? (pathname === `/${subLink.route}` ? activeLink : normalLink) : disabledLink}
                                style={canAccessTab(subLink) && pathname === `/${subLink.route}` ? { backgroundColor: currentColor } : {}}
                              >
                                {subLink.icon}
                                <span className="capitalize text-sm">{subLink.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      key={link.name}
                      onClick={(e) => {
                        if (handleTabClick(link, e)) {
                          handleCloseSideBar();
                        }
                      }}
                      className={canAccessTab(link) ? (pathname === `/${link.route}` ? activeLink : normalLink) : disabledLink}
                      style={canAccessTab(link) && pathname === `/${link.route}` ? { backgroundColor: currentColor } : {}}
                    >
                      {link.icon}
                      <span className="capitalize text-sm">{link.name}</span>
                      {link.badge && (
                        <Badge
                          count={link.badge}
                          size="medium"
                          className="badge-pulse"
                          style={{ marginLeft: 'auto' }}
                        />
                      )}
                    </div>
                  )
                ))}
              </div>
            )) : <div className='flex justify-center items-center h-[90vh]'><Spin /></div>}
          </div>
          <style>{`
          .badge-pulse .ant-badge-count {
          background-color: #ff4d4f;
          animation: pulse 1.4s infinite;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(255, 77, 79, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
          }
        }
          `}</style>
        </>
      )}
    </div>


  );
};



export default Sidebar;
